// Complete ChatContext interface to include multiple file contents
interface ChatContext {
    activeFile: string;
    fileContent: string;
    selectedFiles: string[];
    selectedFileContents?: { [fileName: string]: string };
    fileContents?: { [fileName: string]: string };
    projectStructure: Array<{name: string; type: string}>;
    projectInfo: {
        id: string;
        name: string;
        type: string;
    };
    aiRulesContext?: string;
    documentationContext?: string;
    githubContext?: string;  // NEW: GitHub context
    mcpContext?: string;
}

interface AIRule {
    id: string;
    name: string;
    description: string;
    content: string;
    type: 'custom' | 'url' | 'mcp' | 'template' | 'github';  // ← Add 'github'
    enabled: boolean;
    category: 'security' | 'style' | 'performance' | 'architecture' | 'general';
    url?: string;
    mcpServer?: string;
    lastUpdated?: number;
    status?: 'active' | 'loading' | 'error';
    errorMessage?: string;

    // New GitHub-specific fields
    githubUrl?: string;
    githubPath?: string;
    githubBranch?: string;
    extractionType?: 'readme' | 'contributing' | 'docs' | 'examples' | 'all';
}

// New interface for GitHub-specific data
interface GitHubSource {
    id: string;
    name: string;
    githubUrl: string;
    githubPath?: string;
    githubBranch?: string;
    extractionType: 'readme' | 'contributing' | 'docs' | 'examples' | 'all';
    category: 'security' | 'style' | 'performance' | 'architecture' | 'general';
    description: string;
    status: 'active' | 'loading' | 'error';
    lastFetched?: number;
    contentPreview?: string;
    errorMessage?: string;
}


interface ChatMessage {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: number;
    fileContext?: string[];
    hasFileChanges?: boolean;
    extractedFiles?: { [key: string]: string };
}

interface ConversationHistory {
    id: string;
    projectId: string;
    messages: ChatMessage[];
    createdAt: number;
    updatedAt: number;
    title?: string;
}

interface ChatRequestOptions {
    preferredModel?: 'sonnet-3.7' | 'sonnet-4' | 'opus-4';
    tokenBudget?: number;
}

class ClaudeChatService {
    private baseUrl = 'https://ai.coinnation.io/api/claude';

    /**
     * Send streaming chat message with comprehensive file extraction
     */
    async sendStreamingChatWithFileExtraction(
        apiKey: string,
        messages: ChatMessage[],
        context: ChatContext,
        needsFileExtraction: boolean,
        onChunk: (chunk: string) => void,
        onComplete: (fullResponse: string, files: { [key: string]: string }, modelInfo?: any) => void,
        options?: ChatRequestOptions
    ): Promise<void> {
        // Comprehensive context preparation with validation
        const preparedContext = this.prepareAndValidateContext(context);

        const requestBody = {
            apiKey,
            messages: messages.map(m => ({
                role: m.role,
                content: m.content
            })),
            context: preparedContext,
            needsFileExtraction,
            preferredModel: options?.preferredModel || 'sonnet-4',
            tokenBudget: options?.tokenBudget || 150000
        };

        console.log(`🚀 CHAT: Starting streaming request`);
        console.log(`📁 CONTEXT: ${Object.keys(preparedContext.fileContents || {}).length} files`);
        console.log(`🔧 EXTRACTION: ${needsFileExtraction ? 'ENABLED' : 'DISABLED'}`);
        console.log(`🎯 MODEL: ${requestBody.preferredModel}`);
        console.log(`💰 TOKENS: ${requestBody.tokenBudget}`);

        // Validate context before sending
        const validation = this.validateContext(preparedContext);
        if (!validation.isValid) {
            console.warn(`⚠️ CONTEXT ISSUES:`, validation.warnings);
        }

        const response = await fetch(`${this.baseUrl}/chat/stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.body?.error || 'Failed to get AI response');
        }

        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('Failed to get response stream');
        }

        let fullResponse = '';
        let extractedFiles: { [key: string]: string } = {};
        let modelInfo: any = {};
        const decoder = new TextDecoder();
        let hasCompleted = false;

        try {
            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    console.log(`🏁 STREAM: Natural completion detected`);
                    if (!hasCompleted) {
                        console.log(`✅ TRIGGERING COMPLETION CALLBACK: ${fullResponse.length} chars, ${Object.keys(extractedFiles).length} files`);
                        hasCompleted = true;
                        onComplete(fullResponse, extractedFiles, modelInfo);
                    }
                    break;
                }

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n').filter(line => line.trim());

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6);

                        if (data === '[DONE]' || data === 'DONE' || data.trim() === '') {
                            console.log(`🏁 STREAM: Completion signal received: "${data}"`);
                            if (!hasCompleted) {
                                console.log(`✅ TRIGGERING COMPLETION CALLBACK: ${fullResponse.length} chars, ${Object.keys(extractedFiles).length} files`);
                                hasCompleted = true;
                                onComplete(fullResponse, extractedFiles, modelInfo);
                            }
                            return;
                        }

                        try {
                            const parsed = JSON.parse(data);

                            switch (parsed.type) {
                                case 'connected':
                                    console.log(`🔗 STREAM: Connected (fileExtraction: ${parsed.fileExtractionMode})`);
                                    if (parsed.modelConfig) {
                                        modelInfo = parsed.modelConfig;
                                        console.log(`🤖 MODEL: ${modelInfo.selectedModel}`);
                                    }
                                    break;

                                case 'content_delta':
                                    if (parsed.content) {
                                        fullResponse += parsed.content;
                                        onChunk(parsed.content);
                                    }
                                    break;

                                case 'complete':
                                    console.log(`🏁 STREAM: Complete event received`);
                                    if (parsed.files) {
                                        extractedFiles = parsed.files;
                                        console.log(`📁 FILES: ${Object.keys(extractedFiles).length} extracted`);

                                        Object.entries(extractedFiles).forEach(([fileName, content]) => {
                                            console.log(`   - ${fileName}: ${content.length} chars`);
                                        });
                                    }
                                    if (parsed.modelConfig) {
                                        modelInfo = parsed.modelConfig;
                                    }

                                    if (!hasCompleted) {
                                        console.log(`✅ TRIGGERING COMPLETION CALLBACK: ${fullResponse.length} chars, ${Object.keys(extractedFiles).length} files`);
                                        hasCompleted = true;
                                        onComplete(fullResponse, extractedFiles, modelInfo);
                                    }
                                    return;

                                case 'error':
                                    console.error('🚨 STREAM ERROR:', parsed.message);
                                    throw new Error(parsed.message || 'Streaming error occurred');

                                case 'done':
                                case 'finished':
                                case 'end':
                                    console.log(`🏁 STREAM: Completion event "${parsed.type}" received`);
                                    if (parsed.files) {
                                        extractedFiles = { ...extractedFiles, ...parsed.files };
                                    }
                                    if (!hasCompleted) {
                                        console.log(`✅ TRIGGERING COMPLETION CALLBACK: ${fullResponse.length} chars, ${Object.keys(extractedFiles).length} files`);
                                        hasCompleted = true;
                                        onComplete(fullResponse, extractedFiles, modelInfo);
                                    }
                                    return;
                            }
                        } catch (e) {
                            console.log(`⚠️ STREAM: Skipping invalid JSON chunk: ${data.substring(0, 50)}...`);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('🚨 STREAM ERROR:', error);
            if (!hasCompleted && fullResponse.length > 0) {
                console.log(`⚠️ TRIGGERING COMPLETION CALLBACK ON ERROR: ${fullResponse.length} chars`);
                hasCompleted = true;
                onComplete(fullResponse, extractedFiles, modelInfo);
            }
            throw error;
        } finally {
            reader.releaseLock();

            if (!hasCompleted && fullResponse.length > 0) {
                console.log(`🛡️ SAFETY NET: Triggering completion callback: ${fullResponse.length} chars`);
                hasCompleted = true;
                onComplete(fullResponse, extractedFiles, modelInfo);
            }
        }
    }

    /**
     * Debug stream response for troubleshooting
     */
    async debugStreamResponse(
        apiKey: string,
        messages: ChatMessage[],
        context: ChatContext
    ): Promise<void> {
        const requestBody = {
            apiKey,
            messages: messages.map(m => ({ role: m.role, content: m.content })),
            context: context,
            needsFileExtraction: true,
            preferredModel: 'sonnet-4',
            tokenBudget: 150000
        };

        console.log('🔍 DEBUG: Starting stream analysis...');

        const response = await fetch(`${this.baseUrl}/chat/stream`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody)
        });

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No reader');

        const decoder = new TextDecoder();
        let chunkCount = 0;
        let lastChunks: string[] = [];

        try {
            while (true) {
                const { done, value } = await reader.read();
                chunkCount++;

                console.log(`🔍 CHUNK ${chunkCount}: done=${done}, hasValue=${!!value}`);

                if (done) {
                    console.log(`🔍 STREAM DONE after ${chunkCount} chunks`);
                    console.log(`🔍 LAST 5 CHUNKS:`, lastChunks.slice(-5));
                    break;
                }

                if (value) {
                    const chunk = decoder.decode(value);
                    lastChunks.push(chunk);

                    if (lastChunks.length > 10) {
                        lastChunks = lastChunks.slice(-10);
                    }

                    console.log(`🔍 RAW CHUNK: "${chunk}"`);

                    const lines = chunk.split('\n').filter(line => line.trim());
                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            const data = line.slice(6);
                            console.log(`🔍 DATA LINE: "${data}"`);

                            if (data.includes('complete') || data.includes('done') || data.includes('DONE')) {
                                console.log(`🎯 FOUND COMPLETION SIGNAL: "${data}"`);
                            }
                        }
                    }
                }
            }
        } finally {
            reader.releaseLock();
            console.log(`🔍 DEBUG COMPLETE: ${chunkCount} total chunks processed`);
        }
    }

    /**
     * Send non-streaming chat message with file extraction
     */
    async sendChatWithFileExtraction(
        apiKey: string,
        messages: ChatMessage[],
        context: ChatContext,
        needsFileExtraction: boolean,
        options?: ChatRequestOptions
    ): Promise<{content: string; files: { [key: string]: string }; reasoning?: string; modelInfo?: any}> {
        const preparedContext = this.prepareAndValidateContext(context);

        const requestBody = {
            apiKey,
            messages: messages.map(m => ({
                role: m.role,
                content: m.content
            })),
            context: preparedContext,
            needsFileExtraction,
            preferredModel: options?.preferredModel || 'sonnet-4',
            tokenBudget: options?.tokenBudget || 150000
        };

        console.log(`🚀 CHAT: Non-streaming request`);
        console.log(`📁 CONTEXT: ${Object.keys(preparedContext.fileContents || {}).length} files`);
        console.log(`🔧 EXTRACTION: ${needsFileExtraction ? 'ENABLED' : 'DISABLED'}`);

        const response = await fetch(`${this.baseUrl}/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.body?.error || 'Failed to get AI response');
        }

        const data = await response.json();

        console.log(`✅ NON-STREAM COMPLETE: ${data.body.content.length} chars`);
        console.log(`📁 FILES: ${Object.keys(data.body.files || {}).length} extracted`);

        return {
            content: data.body.content,
            files: data.body.files || {},
            reasoning: data.body.reasoning,
            modelInfo: data.body.modelConfig
        };
    }

    /**
     * Prepare and validate comprehensive context
     */
    private prepareAndValidateContext(context: ChatContext) {
        const preparedContext = {
            activeFile: context.activeFile,
            activeFileContent: context.fileContent,
            projectStructure: context.projectStructure,
            projectInfo: context.projectInfo,
            selectedFiles: context.selectedFiles,
            fileContents: {} as { [fileName: string]: string },
            comprehensiveAIContext: this.buildComprehensiveAIContext(context)
        };

        // Always include the active file
        preparedContext.fileContents[context.activeFile] = context.fileContent;

        // Include content from all selected files with validation
        if (context.selectedFileContents || context.fileContents) {
            const sourceContents = context.fileContents || context.selectedFileContents || {};

            Object.entries(sourceContents).forEach(([fileName, content]) => {
                if (fileName && typeof content === 'string') {
                    preparedContext.fileContents[fileName] = content;
                    console.log(`📄 CONTEXT: Added ${fileName} (${content.length} chars)`);
                } else {
                    console.warn(`⚠️ CONTEXT: Invalid content for ${fileName}`);
                }
            });
        }

        // Ensure we have content for all selected files
        context.selectedFiles.forEach(fileName => {
            if (fileName && !preparedContext.fileContents[fileName]) {
                console.warn(`⚠️ CONTEXT: Missing content for selected file: ${fileName}`);
                preparedContext.fileContents[fileName] = `// Content not available for ${fileName}`;
            }
        });

        console.log(`📊 FINAL CONTEXT: ${Object.keys(preparedContext.fileContents).length} files prepared`);
        console.log(`🎯 AI CONTEXT: ${preparedContext.comprehensiveAIContext ? 'ACTIVE' : 'NONE'}`);

        return preparedContext;
    }

    /**
     * Build comprehensive AI context from all sources
     */
    private buildComprehensiveAIContext(context: ChatContext): string {
        let comprehensiveContext = '';

        // AI Rules Context (existing)
        if (context.aiRulesContext) {
            comprehensiveContext += context.aiRulesContext;
        }

        // Documentation Context (existing)
        if (context.documentationContext) {
            comprehensiveContext += '\n\n=== DOCUMENTATION CONTEXT ===\n';
            comprehensiveContext += 'Reference these documentation sources when providing assistance:\n\n';
            comprehensiveContext += context.documentationContext;
            comprehensiveContext += '\n=== END DOCUMENTATION ===\n\n';
        }

        // NEW: GitHub Context
        if (context.githubContext) {
            comprehensiveContext += '\n\n=== GITHUB REPOSITORY CONTEXT ===\n';
            comprehensiveContext += 'Follow these GitHub repository guidelines and patterns:\n\n';
            comprehensiveContext += context.githubContext;
            comprehensiveContext += '\n=== END GITHUB CONTEXT ===\n\n';
        }

        // MCP Context (existing)
        if (context.mcpContext) {
            comprehensiveContext += '\n\n=== MCP CAPABILITIES ===\n';
            comprehensiveContext += 'Available MCP servers and their tools:\n\n';
            comprehensiveContext += context.mcpContext;
            comprehensiveContext += '\n=== END MCP CAPABILITIES ===\n\n';
        }

        // Context Summary (updated)
        if (comprehensiveContext.length > 0) {
            let summary = '\n\n=== CONTEXT SUMMARY ===\n';
            summary += 'Active Elements:\n';
            if (context.aiRulesContext) summary += '- AI rules and guidelines enforced\n';
            if (context.documentationContext) summary += '- External documentation sources available\n';
            if (context.githubContext) summary += '- GitHub repository guidelines available\n';
            if (context.mcpContext) summary += '- MCP server capabilities connected\n';
            summary += '\nPlease consider all these sources when providing assistance.\n';
            summary += '=== END CONTEXT SUMMARY ===\n\n';

            comprehensiveContext += summary;
        }

        return comprehensiveContext;
    }

    /**
     * Validate context comprehensiveness
     */
    private validateContext(context: any): { isValid: boolean; warnings: string[] } {
        const warnings: string[] = [];
        let totalSize = 0;

        if (!context.fileContents || Object.keys(context.fileContents).length === 0) {
            warnings.push('No file contents available');
            return { isValid: false, warnings };
        }

        Object.entries(context.fileContents).forEach(([fileName, content]: [string, any]) => {
            if (typeof content !== 'string') {
                warnings.push(`Invalid content type for ${fileName}`);
                return;
            }

            totalSize += content.length;

            if (content.length === 0) {
                warnings.push(`Empty file: ${fileName}`);
            } else if (content.length < 50) {
                warnings.push(`Suspiciously small file: ${fileName} (${content.length} chars)`);
            } else if (content.includes('Content not available')) {
                warnings.push(`Placeholder content detected: ${fileName}`);
            }

            if (content.endsWith('...') || content.includes('... (truncated)')) {
                warnings.push(`Potentially truncated file: ${fileName}`);
            }
        });

        // Check comprehensive AI context
        if (context.comprehensiveAIContext) {
            totalSize += context.comprehensiveAIContext.length;
        }

        const maxTotalSize = 1000000; // 1MB total context
        if (totalSize > maxTotalSize) {
            warnings.push(`Large context size: ${Math.round(totalSize / 1024)}KB (may be truncated)`);
        }

        console.log(`🔍 CONTEXT VALIDATION: ${warnings.length} warnings, ${totalSize} total chars`);

        return {
            isValid: warnings.length === 0 || !warnings.some(w => w.includes('not available')),
            warnings
        };
    }

    /**
     * Generate intelligent suggestions based on comprehensive context
     */
    generateSmartSuggestions(
        context: ChatContext,
        recentMessages: ChatMessage[]
    ): string[] {
        const suggestions: string[] = [];
        const allFileContents = context.selectedFileContents || context.fileContents || { [context.activeFile]: context.fileContent };
        const fileCount = Object.keys(allFileContents).length;

        console.log(`💡 SUGGESTIONS: Analyzing ${fileCount} files for smart suggestions`);

        // Multi-file comprehensive suggestions
        if (fileCount > 1) {
            suggestions.push("Analyze all selected files and fix any issues");
            suggestions.push("Refactor code across all files for consistency");
            suggestions.push("Add comprehensive error handling to all components");
            suggestions.push("Optimize the entire codebase structure");
        }

        // Problem-solving suggestions
        suggestions.push("Fix any compilation or runtime errors in the code");
        suggestions.push("Debug and resolve the reported issue");
        suggestions.push("Improve code quality and add missing features");
        suggestions.push("Add proper TypeScript types and interfaces");

        // Feature improvement suggestions
        const allContent = Object.values(allFileContents).join('\n').toLowerCase();

        if (allContent.includes('todo') || allContent.includes('fixme')) {
            suggestions.push("Implement all TODO items and fix FIXME comments");
        }

        if (allContent.includes('button') || allContent.includes('onclick')) {
            suggestions.push("Improve button functionality and user interactions");
        }

        if (allContent.includes('form') || allContent.includes('input')) {
            suggestions.push("Add form validation and error handling");
        }

        if (allContent.includes('api') || allContent.includes('fetch')) {
            suggestions.push("Improve API integration and error handling");
        }

        // Recent conversation context
        const hasErrors = recentMessages.some(m =>
            m.content.toLowerCase().includes('error') ||
            m.content.toLowerCase().includes('bug') ||
            m.content.toLowerCase().includes('issue')
        );

        if (hasErrors) {
            suggestions.push("Investigate and resolve the reported errors");
            suggestions.push("Add defensive programming practices");
        }

        // File-type specific suggestions
        const fileTypes = Object.keys(allFileContents).map(f => f.split('.').pop()?.toLowerCase()).filter(Boolean);
        const uniqueTypes = [...new Set(fileTypes)];

        if (uniqueTypes.includes('mo') && uniqueTypes.includes('tsx')) {
            suggestions.push("Ensure proper backend-frontend integration");
            suggestions.push("Add type safety between Motoko and TypeScript");
        }

        if (uniqueTypes.includes('css') && uniqueTypes.includes('tsx')) {
            suggestions.push("Improve styling and responsive design");
            suggestions.push("Add consistent theming across components");
        }

        // Always include comprehensive suggestions
        suggestions.push("Perform comprehensive code review and optimization");
        suggestions.push("Add complete documentation and comments");

        return suggestions.slice(0, 8);
    }

    /**
     * Format conversation for storage with comprehensive metadata
     */
    formatConversationForStorage(
        projectId: string,
        messages: ChatMessage[],
        title?: string
    ): ConversationHistory {
        return {
            id: `conv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            projectId,
            messages: messages.map(msg => ({
                ...msg,
                fileContext: msg.fileContext || [],
                hasFileChanges: msg.hasFileChanges || false,
                extractedFiles: msg.extractedFiles || {}
            })),
            createdAt: Date.now(),
            updatedAt: Date.now(),
            title: title || this.generateConversationTitle(messages)
        };
    }

    /**
     * Generate a descriptive title for the conversation
     */
    private generateConversationTitle(messages: ChatMessage[]): string {
        const firstUserMessage = messages.find(m => m.role === 'user');
        if (!firstUserMessage) {
            return 'New Conversation';
        }

        let title = firstUserMessage.content.trim();

        // Extract key action words for better titles
        const actionMatch = title.match(/\b(fix|debug|create|add|improve|optimize|refactor|implement|analyze)\b/i);
        if (actionMatch) {
            const action = actionMatch[0];
            const shortened = title.substring(0, 40).trim();
            title = `${action}: ${shortened}`;
        }

        if (title.length > 60) {
            title = title.substring(0, 57) + '...';
        }

        title = title.replace(/\n/g, ' ').replace(/\s+/g, ' ');

        return title;
    }

    /**
     * Save conversation to project storage
     */
    async saveConversation(
        userActor: any,
        userPrincipal: any,
        projectId: string,
        conversation: ConversationHistory,
        versionId?: string
    ): Promise<void> {
        const conversationPath = `.chat/conversations`;
        const fileName = `${conversation.id}.json`;
        const content = JSON.stringify(conversation, null, 2);

        const versionParam = versionId ? [versionId] : [];

        const result = await userActor.createCodeArtifact(
            userPrincipal,
            projectId,
            fileName,
            { Text: content },
            'application/json',
            'json',
            conversationPath,
            versionParam
        );

        if ('err' in result) {
            throw new Error(`Failed to save conversation: ${result.err}`);
        }
    }

    /**
     * Update existing conversation
     */
    async updateConversation(
        userActor: any,
        userPrincipal: any,
        projectId: string,
        conversation: ConversationHistory,
        versionId?: string
    ): Promise<void> {
        const conversationPath = `.chat/conversations`;
        const fileName = `${conversation.id}.json`;

        conversation.updatedAt = Date.now();

        const content = JSON.stringify(conversation, null, 2);
        const versionParam = versionId ? [versionId] : [];

        const result = await userActor.updateCodeArtifact(
            userPrincipal,
            projectId,
            fileName,
            { Text: content },
            conversationPath,
            versionParam
        );

        if ('err' in result) {
            throw new Error(`Failed to update conversation: ${result.err}`);
        }
    }

    /**
     * BACKWARD COMPATIBILITY: Original chat methods
     */
    async sendChatMessage(
        apiKey: string,
        messages: ChatMessage[],
        context: ChatContext
    ): Promise<{content: string; reasoning?: string}> {
        const result = await this.sendChatWithFileExtraction(apiKey, messages, context, false);
        return {
            content: result.content,
            reasoning: result.reasoning
        };
    }

    async sendStreamingChatMessage(
        apiKey: string,
        messages: ChatMessage[],
        context: ChatContext,
        onChunk: (chunk: string) => void,
        onComplete: (fullResponse: string) => void
    ): Promise<void> {
        await this.sendStreamingChatWithFileExtraction(
            apiKey,
            messages,
            context,
            false,
            onChunk,
            (fullResponse, files) => onComplete(fullResponse)
        );
    }

    /**
     * UTILITY: Extract code blocks from AI response
     */
    extractCodeBlocks(content: string): Array<{language: string; code: string}> {
        const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
        const blocks: Array<{language: string; code: string}> = [];
        let match;

        while ((match = codeBlockRegex.exec(content)) !== null) {
            blocks.push({
                language: match[1] || 'text',
                code: match[2].trim()
            });
        }

        return blocks;
    }

    /**
     * UTILITY: Check if response contains actionable code changes
     */
    hasCodeChanges(content: string): boolean {
        return content.includes('```') ||
            content.toLowerCase().includes('create file') ||
            content.toLowerCase().includes('modify file') ||
            content.toLowerCase().includes('update file');
    }

    /**
     * DEBUG: Log comprehensive context information
     */
    debugContext(context: ChatContext): void {
        console.group('🔍 COMPREHENSIVE CONTEXT DEBUG');

        console.log('Active File:', context.activeFile);
        console.log('Selected Files:', context.selectedFiles);
        console.log('Project Structure:', context.projectStructure.length, 'files total');
        console.log('Project Info:', context.projectInfo);

        const fileContents = context.fileContents || context.selectedFileContents || {};
        console.log('File Contents Available:');
        Object.entries(fileContents).forEach(([fileName, content]) => {
            if (typeof content === 'string') {
                console.log(`  - ${fileName}: ${content.length} chars`);
                if (content.length < 100) {
                    console.log(`    Content: ${content}`);
                } else {
                    console.log(`    Preview: ${content.substring(0, 100)}...`);
                }
            } else {
                console.log(`  - ${fileName}: INVALID CONTENT TYPE`);
            }
        });

        if (context.aiRulesContext) {
            console.log('AI Rules Context:', context.aiRulesContext.length, 'chars');
        }

        if (context.documentationContext) {
            console.log('Documentation Context:', context.documentationContext.length, 'chars');
        }

        if (context.mcpContext) {
            console.log('MCP Context:', context.mcpContext.length, 'chars');
        }

        const validation = this.validateContext({ fileContents });
        console.log('Context Validation:', validation);

        console.groupEnd();
    }
}

export default ClaudeChatService;